import { useContext, useLayoutEffect, useRef, useState } from 'react';
import { MyBetSlipContext } from './BetSlipHooks';
import cloneDeep from 'lodash/cloneDeep';
import { checkPreviewBetOddsUpdated, isOddsUpdated } from './useBetSlipPush/common';
import { clearBetLineOddsUpdTs } from '../Common/utils';
import moment from 'moment';


// Currently only FB has push, and the data structure of racing is slightly different, so only FB's bet is processed.
const isFbBetLine = (betLine) => {
    return betLine.prod === 'FB';
};

const getLastSellingOddsDiff = (comb) => {
    const { oddsVal, lastSellingOddsVal } = comb;
    if (isNaN(oddsVal) || isNaN(lastSellingOddsVal)) {
        return 0;
    }
    const oddsDiff = parseFloat(oddsVal) - parseFloat(lastSellingOddsVal);
    return oddsDiff;
};

const checkLastSellingOddsUpdated = (data) => {
    if (data.betObj.betType == 'ALUP') {
        return data.betObj.betList.some((betItem) => {
            return betItem.combs.some((comb) => getLastSellingOddsDiff(comb));
        });
    } else {
        return data.betObj.combs.some((comb) => getLastSellingOddsDiff(comb));
    }
};

const getNeedResetAcceptanceBetLineIds = ({ betBaskets, currentBasket, currentBasketSnapshotRef }) => {
    const needSetDefaultAcceptanceBetLineIds = [];
    const currentSnapshot = currentBasketSnapshotRef.current;

    const hasOddsUpdated = (currentItem, previousItem) => {
        if (!previousItem) return false;

        const compareCombs = (combs, preCombs) =>
            combs.some((comb) => {
                if (comb.isStopSell) {
                    return false;
                }
                const preComb = preCombs.find((item) => item.id === comb.id);
                return preComb && preComb.oddsVal !== comb.oddsVal;
            });

        if (currentItem.betObj.betType === 'ALUP') {
            return currentItem.betObj.betList.some((betItem) => {
                const preBetItem = previousItem.betObj.betList.find((item) => item.id === betItem.id);
                return preBetItem && compareCombs(betItem.combs, preBetItem.combs);
            });
        }

        return compareCombs(currentItem.betObj.combs, previousItem.betObj.combs);
    };

    betBaskets[currentBasket].forEach((betLineItem) => {
        if (!isFbBetLine(betLineItem) || betLineItem.onlyDisplayOnBetSlip) {
            return;
        }
        const previousBetLine = currentSnapshot?.find((item) => item.id === betLineItem.id);

        if (
            !betLineItem.betObj.isStopSell &&
            previousBetLine?.betObj?.isStopSell &&
            checkLastSellingOddsUpdated(previousBetLine)
        ) {
            needSetDefaultAcceptanceBetLineIds.push(betLineItem.id);
            return;
        }

        if (hasOddsUpdated(betLineItem, previousBetLine)) {
            needSetDefaultAcceptanceBetLineIds.push(betLineItem.id);
        }
    });

    return needSetDefaultAcceptanceBetLineIds;
};

const checkShouldShowAcceptAllCheckbox = (data) => {
    return data.some((item) => {
        if (!isFbBetLine(item) || item.onlyDisplayOnBetSlip) {
            return false;
        }
        return checkPreviewBetOddsUpdated(item);
    });
};

const checkIsAcceptAll = (currentBasketData) => {
    return !currentBasketData.some((item) => {
        if (!isFbBetLine(item) || item.onlyDisplayOnBetSlip) {
            return false;
        }
        if (!checkPreviewBetOddsUpdated(item)) {
            return false;
        }
        return !item.isAcceptance;
    });
};

const getStopSellOnPreviewBetIds = ({ betBaskets, currentBasket, currentBasketSnapshotRef }) => {
    const stopSellOnPreviewBetIds = [];
    const resetStopSellOnPreviewBetIds = [];
    const currentSnapshot = currentBasketSnapshotRef.current;

    betBaskets[currentBasket].forEach((betLineItem) => {
        if (!isFbBetLine(betLineItem) || betLineItem.onlyDisplayOnBetSlip) {
            return;
        }
        const previousBetLine = currentSnapshot?.find((item) => item.id === betLineItem.id);
        if (!previousBetLine) {
            return;
        }
        if (betLineItem.betObj.isStopSell && !previousBetLine?.betObj?.isStopSell) {
            stopSellOnPreviewBetIds.push(betLineItem.id);
        }

        if (
            !betLineItem.betObj.isStopSell &&
            previousBetLine?.betObj?.isStopSell &&
            previousBetLine.isStopSellOnPreview
        ) {
            resetStopSellOnPreviewBetIds.push(betLineItem.id);
        }
    });
    return {
        stopSellOnPreviewBetIds,
        resetStopSellOnPreviewBetIds
    };
};

const getNeedRemoveOldOddsBetLineIds = ({ betBaskets, currentBasket, currentBasketSnapshotRef }) => {
    const result = [];
    const currentSnapshot = currentBasketSnapshotRef.current;

    betBaskets[currentBasket].forEach((betLineItem) => {
        if (!isFbBetLine(betLineItem)) {
            return;
        }
        const previousBetLine = currentSnapshot?.find((item) => item.id === betLineItem.id);
        if (
            !betLineItem.betObj.isStopSell &&
            previousBetLine?.betObj?.isStopSell &&
            !previousBetLine.isStopSellOnPreview
        ) {
            result.push(betLineItem.id);
            return;
        }
    });

    return result;
};

// a bet was stop sell in betslip and restart sell in preview will only be display in betslip
const getNeedTagOnlyDisplayOnBetSlipBetIds = ({ betBaskets, currentBasket, currentBasketSnapshotRef }) => {
    const result = [];
    const currentSnapshot = currentBasketSnapshotRef.current;

    betBaskets[currentBasket].forEach((betLineItem) => {
        if (!isFbBetLine(betLineItem)) {
            return;
        }
        const previousBetLine = currentSnapshot?.find((item) => item.id === betLineItem.id);
        if (
            !betLineItem.betObj.isStopSell &&
            previousBetLine?.betObj?.isStopSell &&
            !previousBetLine.isStopSellOnPreview
        ) {
            result.push(betLineItem.id);
            return;
        }
    });

    return result;
};

export default function usePreviewBetAcceptance({ isPreview }) {
    const { betBaskets, setBetBaskets, currentBasket } = useContext(MyBetSlipContext);
    const currentBasketSnapshotRef = useRef();
    const [showAcceptAllCheckbox, setShowAcceptAllCheckbox] = useState(false);
    const [isAcceptAllChange, setIsAcceptAllChange] = useState(false);

    useLayoutEffect(() => {
        if (!isPreview) {
            setShowAcceptAllCheckbox(false);
            return;
        }
        const onlyDisplayOnBetSlipBetIds = window.globalConfig.ENABLE_BET_ACCEPTANCE_PARSE2 ? getNeedTagOnlyDisplayOnBetSlipBetIds({
            betBaskets,
            currentBasket,
            currentBasketSnapshotRef
        }) : [];
        const needRemoveOldOddsBetLineIds = !window.globalConfig.ENABLE_BET_ACCEPTANCE_PARSE2 ? getNeedRemoveOldOddsBetLineIds({
            betBaskets,
            currentBasket,
            currentBasketSnapshotRef
        }) : [];
        const needResetAcceptanceBetLineIds = getNeedResetAcceptanceBetLineIds({
            betBaskets,
            currentBasket,
            currentBasketSnapshotRef
        });
        const { stopSellOnPreviewBetIds, resetStopSellOnPreviewBetIds } = getStopSellOnPreviewBetIds({
            betBaskets,
            currentBasket,
            currentBasketSnapshotRef
        });
        let newCurrentBasketData = betBaskets[currentBasket];
        if (onlyDisplayOnBetSlipBetIds.length > 0) {
            newCurrentBasketData = tagBetLineOnlyDisplayOnBetSlip(newCurrentBasketData, onlyDisplayOnBetSlipBetIds);
        }
        if (needRemoveOldOddsBetLineIds.length > 0) {
            newCurrentBasketData = removeBetLineOldOdds(newCurrentBasketData, needRemoveOldOddsBetLineIds);
        }
        if (needResetAcceptanceBetLineIds.length > 0) {
            newCurrentBasketData = resetIsAcceptance(newCurrentBasketData, needResetAcceptanceBetLineIds);
        }
        if (stopSellOnPreviewBetIds.length > 0 || resetStopSellOnPreviewBetIds.length > 0) {
            newCurrentBasketData = updateIsStopSellOnPreview(
                newCurrentBasketData,
                stopSellOnPreviewBetIds,
                resetStopSellOnPreviewBetIds
            );
        }
        if (newCurrentBasketData !== betBaskets[currentBasket]) {
            updateCurrentBasket(newCurrentBasketData);
        }
        currentBasketSnapshotRef.current = cloneDeep(betBaskets[currentBasket]);

        setShowAcceptAllCheckbox(checkShouldShowAcceptAllCheckbox(betBaskets[currentBasket]));
        setIsAcceptAllChange(checkIsAcceptAll(betBaskets[currentBasket]));
    }, [betBaskets, isPreview, currentBasket]);

    const updateCurrentBasket = (newData) => {
        const baskets = [...betBaskets];
        baskets[currentBasket] = newData;
        setBetBaskets(baskets);

        console.debug('baskets', baskets);
    };

    const initPreviewBetAcceptanceState = () => {
        const newData = betBaskets[currentBasket].map((item) => {
            if (!isFbBetLine(item)) {
                return item;
            }
            if (item.betObj.betType == 'ALUP') {
                item.betObj.betList = item.betObj.betList.map((betItem) => {
                    betItem.combs = betItem.combs.map((comb) => {
                        return {
                            ...comb,
                            oldOdds: undefined
                        };
                    });

                    return betItem;
                });
            } else {
                item.betObj.combs = item.betObj.combs.map((comb) => {
                    return {
                        ...comb,
                        oldOdds: undefined
                    };
                });
            }

            return {
                ...item,
                isAcceptance: undefined,
                isNotPlaced: undefined,
                reason: undefined,
                isStopSellOnPreview: false
            };
        });
        setIsAcceptAllChange(false);
        updateCurrentBasket(newData);
        currentBasketSnapshotRef.current = null;
        return newData;
    };

    const tagBetLineOnlyDisplayOnBetSlip = (currentBaskets, ids) => {
        const result = currentBaskets.map((item) => {
            if (ids.includes(item.id)) {
                return {
                    ...item,
                    onlyDisplayOnBetSlip: true
                };
            }

            return item;
        });
        return result;
    };

    const removeBetLineOldOdds = (currentBaskets, ids) => {
        const result = currentBaskets.map((item) => {
            if (!ids.includes(item.id)) {
                return item;
            }
            if (item.betObj.betType == 'ALUP') {
                item.betObj.betList = item.betObj.betList.map((betItem) => {
                    betItem.combs = betItem.combs.map((comb) => {
                        return {
                            ...comb,
                            oldOdds: undefined
                        };
                    });

                    return betItem;
                });
            } else {
                item.betObj.combs = item.betObj.combs.map((comb) => {
                    return {
                        ...comb,
                        oldOdds: undefined
                    };
                });
            }

            return {
                ...item
            }
        });
        return result;
    };

    const clearCounterOfferData = (betLine) => {
        const result = {
            ...betLine,
            counterOfferAmountChange: false,
            counterOfferOddsChange: false,
            isCounterOffer: false,
        };
        if (result.counterOfferData) {
            delete result['counterOfferData'];
        }
        return result;
    };

    const resetIsAcceptance = (currentBaskets, ids) => {
        const result = currentBaskets.map((item) => {
            if (!ids.includes(item.id)) {
                return item;
            }
            return {
                ...clearCounterOfferData(item),
                isAcceptance: false
            };
        });
        return result;
    };

    const updateIsStopSellOnPreview = (currentBaskets, ids, resetIds) => {
        const result = currentBaskets.map((item) => {
            if (ids.includes(item.id)) {
                return {
                    ...clearCounterOfferData(item),
                    isStopSellOnPreview: true
                };
            }

            if (resetIds.includes(item.id)) {
                return {
                    ...item,
                    isStopSellOnPreview: false
                };
            }

            return item;
        });
        return result;
    };

    const changePreviewBetAcceptance = (id) => {
        const newData = betBaskets[currentBasket].map((item) => {
            if (id === item.id) {
                return {
                    ...item,
                    isAcceptance: !item.isAcceptance
                };
            }
            return item;
        });
        updateCurrentBasket(newData);
    };

    const toggleAcceptAllChange = () => {
        const newData = betBaskets[currentBasket].map((item) => {
            if (!isFbBetLine(item) || item.onlyDisplayOnBetSlip) {
                return item;
            }

            const hasOddsUpdated = checkPreviewBetOddsUpdated(item);
            if (!hasOddsUpdated) {
                return item;
            }

            return {
                ...item,
                isAcceptance: !isAcceptAllChange
            };
        });
        updateCurrentBasket(newData);
        setIsAcceptAllChange(!isAcceptAllChange);
    };

    const resetPreviewBetAcceptanceState = () => {
        const oddsUpdTs = moment();
        let newData = betBaskets[currentBasket].map((item) => {
            if (!isFbBetLine(item)) {
                return item;
            }

            if (window.globalConfig.ENABLE_BET_ACCEPTANCE_PARSE2) {
                return {
                    ...clearBetLineOddsUpdTs(item),
                    isAcceptance: undefined,
                    isStopSellOnPreview: false,
                    onlyDisplayOnBetSlip: undefined
                };
            }

            if (item.betObj.betType == 'ALUP') {
                item.betObj.betList = item.betObj.betList.map((betItem) => {
                    betItem.combs = betItem.combs.map((comb) => {
                        if (!comb.isStopSell && isOddsUpdated(comb)) {
                            return {
                                ...comb,
                                oddsUpdTs
                            };
                        }
                        return comb;
                    });

                    return betItem;
                });
            } else {
                item.betObj.combs = item.betObj.combs.map((comb) => {
                    if (!comb.isStopSell && isOddsUpdated(comb)) {
                        return {
                            ...comb,
                            oddsUpdTs
                        };
                    }
                    return comb;
                });
            }

            return {
                ...item,
                isAcceptance: undefined,
                isStopSellOnPreview: false,
                onlyDisplayOnBetSlip: undefined
            };
        });
        setIsAcceptAllChange(false);
        updateCurrentBasket(newData);
        currentBasketSnapshotRef.current = null;
    };

    return {
        initPreviewBetAcceptanceState,
        resetPreviewBetAcceptanceState,
        showAcceptAllCheckbox,
        isAcceptAllChange,
        toggleAcceptAllChange,
        onPreviewBetAcceptanceClick: changePreviewBetAcceptance
    };
}
